import React, { useState, useEffect, useRef } from 'react';
import { Box, Paper, Typography, IconButton, Collapse, Chip, LinearProgress, Button, Tabs, Tab, Badge } from '@mui/material';
import { ExpandLess, ExpandMore, Close, Refresh, Timeline, Computer, Satellite, Analytics } from '@mui/icons-material';

interface LogEntry {
  timestamp: string;
  level: string;
  message: string;
  data?: any;
}

interface CategorizedLogs {
  satellite_logs: LogEntry[];
  feature_logs: LogEntry[];
  model_logs: LogEntry[];
  prediction_logs: LogEntry[];
  error_logs: LogEntry[];
  general_logs: LogEntry[];
}

interface StreamEvent {
  type: 'connection' | 'log' | 'progress' | 'complete' | 'error' | 'disconnect';
  analysis_id?: string;
  timestamp?: string;
  level?: string;
  message: string;
  data?: any;
  progress?: number;
  status?: string;
  current_phase?: string;
}

interface EnhancedAnalysisLogPanelProps {
  analysisId: string;
  isVisible: boolean;
  onClose: () => void;
}

const EnhancedAnalysisLogPanel: React.FC<EnhancedAnalysisLogPanelProps> = ({
  analysisId,
  isVisible,
  onClose
}) => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [categorizedLogs, setCategorizedLogs] = useState<CategorizedLogs | null>(null);
  const [isExpanded, setIsExpanded] = useState(true);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState<string>('initializing');
  const [showAuthButton, setShowAuthButton] = useState(false);
  const [geeAuthenticated, setGeeAuthenticated] = useState(true);
  const [currentTab, setCurrentTab] = useState(0);
  const [useRealTimeStream, setUseRealTimeStream] = useState(true);
  const logContainerRef = useRef<HTMLDivElement>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const startLogFetchingRef = useRef<() => void>(() => {});
  const stopLogFetchingRef = useRef<() => void>(() => {});

  // Auto-scroll to bottom when new logs arrive
  const scrollToBottom = () => {
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  };

  // Setup Server-Sent Events for real-time log streaming
  const setupEventStream = () => {
    if (!analysisId || eventSourceRef.current) return;

    const eventSource = new EventSource(
      `http://localhost:8000/api/enhanced-analysis/analysis/${analysisId}/logs/stream`
    );

    eventSourceRef.current = eventSource;

    eventSource.onmessage = (event) => {
      try {
        const data: StreamEvent = JSON.parse(event.data);
        
        switch (data.type) {
          case 'connection':
            break;
            
          case 'log':
            if (data.timestamp && data.level && data.message) {
              const newLog: LogEntry = {
                timestamp: data.timestamp,
                level: data.level,
                message: data.message,
                data: data.data
              };
              setLogs(prev => [...prev, newLog]);
            }
            break;
            
          case 'progress':
            if (data.progress !== undefined) {
              setProgress(data.progress);
            }
            if (data.status) {
              setStatus(data.status);
            }
            break;
            
          case 'complete':
            setStatus(data.status || 'completed');
            setProgress(100);
            // Keep connection open briefly for any final logs
            setTimeout(() => {
              closeEventStream();
              // Fetch categorized logs for better viewing
              fetchCategorizedLogs();
            }, 2000);
            break;
            
          case 'error':
            console.error('Stream error:', data.message);
            closeEventStream();
            // Fall back to polling
            setUseRealTimeStream(false);
            startPolling();
            break;
            
          case 'disconnect':
            closeEventStream();
            break;
        }
      } catch (error) {
        console.error('Error parsing stream event:', error);
      }
    };

    eventSource.onerror = (error) => {
      console.error('EventSource error:', error);
      closeEventStream();
      // Fall back to polling
      setUseRealTimeStream(false);
      startPolling();
    };
  };

  // Close event stream
  const closeEventStream = () => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
  };

  // Fetch categorized logs for better organization
  const fetchCategorizedLogs = async () => {
    if (!analysisId) return;

    try {
      const response = await fetch(
        `http://localhost:8000/api/enhanced-analysis/analysis/${analysisId}/logs/detailed`
      );
      
      if (!response.ok) {
        console.error('Failed to fetch categorized logs:', response.status);
        return;
      }

      const data = await response.json();
      
      if (data.categorized_logs) {
        setCategorizedLogs(data.categorized_logs);
      }
      
      // Handle GEE authentication status
      if (data.gee_authenticated !== undefined) {
        setGeeAuthenticated(data.gee_authenticated);
      }
      
    } catch (error) {
      console.error('Error fetching categorized logs:', error);
    }
  };

  // Fallback: Fetch logs from the backend (polling mode)
  const fetchLogs = async () => {
    if (!analysisId) return;

    try {
      const response = await fetch(
        `http://localhost:8000/api/enhanced-analysis/analysis/${analysisId}/logs`
      );
      
      if (!response.ok) {
        console.error('Failed to fetch logs:', response.status);
        return;
      }

      const data = await response.json();
      
      // Update logs
      if (data.logs && Array.isArray(data.logs)) {
        setLogs(data.logs);
      }
      
      // Update progress and status
      if (data.progress !== undefined) {
        setProgress(data.progress);
      }
      if (data.current_status) {
        setStatus(data.current_status);
        // Stop polling if analysis is complete or failed
        if (data.current_status === 'completed' || data.current_status === 'failed') {
          stopPolling();
          fetchCategorizedLogs();
        }
      }
      
      // Handle GEE authentication status
      if (data.gee_authenticated !== undefined) {
        setGeeAuthenticated(data.gee_authenticated);
      }
      if (data.show_auth_button !== undefined) {
        setShowAuthButton(data.show_auth_button);
      }
      
    } catch (error) {
      console.error('Error fetching logs:', error);
    }
  };

  // Start polling for logs (fallback mode)
  const startPolling = () => {
    if (pollIntervalRef.current) return;
    
    // Initial fetch
    fetchLogs();
    
    // Set up polling interval (every 2 seconds)
    pollIntervalRef.current = setInterval(fetchLogs, 2000);
  };

  // Stop polling
  const stopPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  };

  // Start appropriate log fetching method
  const startLogFetching = () => {
    if (useRealTimeStream) {
      setupEventStream();
    } else {
      startPolling();
    }
  };

  // Stop all log fetching
  const stopLogFetching = () => {
    closeEventStream();
    stopPolling();
  };
  startLogFetchingRef.current = startLogFetching;
  stopLogFetchingRef.current = stopLogFetching;

  // Handle GEE authentication
  const handleAuthenticateGEE = async () => {
    try {
      const response = await fetch(
        'http://localhost:8000/api/enhanced-analysis/authenticate-gee',
        { method: 'POST' }
      );
      
      if (response.ok) {
        const data = await response.json();
        // Could open a modal or show instructions to the user
        alert(data.instructions);
      }
    } catch (error) {
      console.error('Failed to get GEE authentication instructions:', error);
    }
  };

  // Start/stop log fetching based on visibility and analysis ID
  useEffect(() => {
    if (isVisible && analysisId) {
      startLogFetchingRef.current();
    } else {
      stopLogFetchingRef.current();
    }

    // Cleanup on unmount
    return () => {
      stopLogFetchingRef.current();
    };
  }, [analysisId, isVisible, useRealTimeStream]);

  // Auto-scroll when new logs arrive
  useEffect(() => {
    scrollToBottom();
  }, [logs]);

  // Get status color
  const getStatusColor = (status: string): 'success' | 'error' | 'warning' | 'info' | 'default' => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'running':
        return 'warning';
      default:
        return 'info';
    }
  };

  // Get log level icon
  const getLogIcon = (level: string): string => {
    switch (level) {
      case 'error':
        return '❌';
      case 'warning':
        return '⚠️';
      case 'info':
        return '📊';
      case 'success':
        return '✅';
      default:
        return '📝';
    }
  };

  // Get log level color
  const getLogColor = (level: string): string => {
    switch (level) {
      case 'error':
        return '#ff6b6b';
      case 'warning':
        return '#ffd43b';
      case 'success':
        return '#51cf66';
      default:
        return '#ffffff';
    }
  };

  // Get logs for current tab
  const getCurrentLogs = (): LogEntry[] => {
    if (!categorizedLogs) return logs;
    
    switch (currentTab) {
      case 0: return logs; // All logs
      case 1: return categorizedLogs.satellite_logs;
      case 2: return categorizedLogs.feature_logs;
      case 3: return categorizedLogs.model_logs;
      case 4: return categorizedLogs.prediction_logs;
      case 5: return categorizedLogs.error_logs;
      default: return logs;
    }
  };

  // Get count for tab badge
  const getTabCount = (tabIndex: number): number => {
    if (!categorizedLogs) return 0;
    
    switch (tabIndex) {
      case 1: return categorizedLogs.satellite_logs.length;
      case 2: return categorizedLogs.feature_logs.length;
      case 3: return categorizedLogs.model_logs.length;
      case 4: return categorizedLogs.prediction_logs.length;
      case 5: return categorizedLogs.error_logs.length;
      default: return 0;
    }
  };

  if (!isVisible) return null;

  return (
    <Paper
      elevation={4}
      sx={{
        position: 'fixed',
        bottom: 20,
        right: 20,
        width: 600,
        maxHeight: categorizedLogs ? 550 : 500,
        zIndex: 1300,
        backgroundColor: '#1e1e1e',
        color: '#ffffff',
        border: '1px solid #444'
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: '1px solid #444',
          backgroundColor: '#2d2d2d'
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" sx={{ color: '#ffffff' }}>
              Enhanced Analysis Progress
            </Typography>
            <Chip
              size="small"
              label={status}
              color={getStatusColor(status)}
              variant="outlined"
            />
            <Chip
              size="small"
              label={`${logs.length} events`}
              color="info"
              variant="outlined"
            />
            {useRealTimeStream ? (
              <Chip
                size="small"
                label="Live Stream"
                color="success"
                variant="outlined"
                icon={<Timeline />}
              />
            ) : (
              <Chip
                size="small"
                label="Polling Mode"
                color="warning"
                variant="outlined"
                icon={<Refresh />}
              />
            )}
            {!geeAuthenticated && (
              <Chip
                size="small"
                label="GEE Auth Required"
                color="error"
                variant="outlined"
              />
            )}
          </Box>
          
          {/* Progress Bar */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LinearProgress 
              variant="determinate" 
              value={progress} 
              sx={{ 
                flex: 1, 
                height: 8,
                backgroundColor: '#444',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: status === 'failed' ? '#ff6b6b' : '#4caf50'
                }
              }} 
            />
            <Typography variant="caption" sx={{ color: '#888', minWidth: '45px' }}>
              {progress}%
            </Typography>
          </Box>
          
          {/* Auth Button if needed */}
          {showAuthButton && !geeAuthenticated && (
            <Button
              size="small"
              variant="outlined"
              color="warning"
              onClick={handleAuthenticateGEE}
              startIcon={<Refresh />}
              sx={{ alignSelf: 'flex-start' }}
            >
              Authenticate Google Earth Engine
            </Button>
          )}
        </Box>
        
        <Box>
          <IconButton
            size="small"
            onClick={() => setIsExpanded(!isExpanded)}
            sx={{ color: '#ffffff', mr: 1 }}
          >
            {isExpanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
          <IconButton
            size="small"
            onClick={onClose}
            sx={{ color: '#ffffff' }}
          >
            <Close />
          </IconButton>
        </Box>
      </Box>

      {/* Log Content */}
      <Collapse in={isExpanded}>
        {/* Log Category Tabs */}
        {categorizedLogs && (
          <Box sx={{ borderBottom: 1, borderColor: '#444' }}>
            <Tabs 
              value={currentTab} 
              onChange={(_, newValue) => setCurrentTab(newValue)}
              sx={{
                '& .MuiTab-root': { color: '#888', minHeight: 40 },
                '& .Mui-selected': { color: '#ffffff' },
                '& .MuiTabs-indicator': { backgroundColor: '#4caf50' }
              }}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab 
                label="All" 
                icon={<Timeline />}
                iconPosition="start"
                sx={{ fontSize: '0.75rem' }}
              />
              <Tab 
                label={
                  <Badge badgeContent={getTabCount(1)} color="info" max={99}>
                    Satellite
                  </Badge>
                } 
                icon={<Satellite />}
                iconPosition="start"
                sx={{ fontSize: '0.75rem' }}
              />
              <Tab 
                label={
                  <Badge badgeContent={getTabCount(2)} color="info" max={99}>
                    Features
                  </Badge>
                } 
                icon={<Analytics />}
                iconPosition="start"
                sx={{ fontSize: '0.75rem' }}
              />
              <Tab 
                label={
                  <Badge badgeContent={getTabCount(3)} color="info" max={99}>
                    Models
                  </Badge>
                } 
                icon={<Computer />}
                iconPosition="start"
                sx={{ fontSize: '0.75rem' }}
              />
              <Tab 
                label={
                  <Badge badgeContent={getTabCount(4)} color="info" max={99}>
                    Predictions
                  </Badge>
                } 
                sx={{ fontSize: '0.75rem' }}
              />
              <Tab 
                label={
                  <Badge badgeContent={getTabCount(5)} color="error" max={99}>
                    Errors
                  </Badge>
                } 
                sx={{ fontSize: '0.75rem' }}
              />
            </Tabs>
          </Box>
        )}
        
        <Box
          ref={logContainerRef}
          sx={{
            height: categorizedLogs ? 300 : 350,
            overflowY: 'auto',
            p: 1,
            backgroundColor: '#1e1e1e',
            fontFamily: 'monospace',
            fontSize: '0.875rem'
          }}
        >
          {getCurrentLogs().length === 0 ? (
            <Typography sx={{ color: '#888', p: 2, textAlign: 'center' }}>
              {logs.length === 0 ? 'Waiting for analysis logs...' : 'No logs in this category'}
            </Typography>
          ) : (
            getCurrentLogs().map((log, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                  py: 0.5,
                  px: 1,
                  backgroundColor: log.level === 'error' ? 'rgba(255, 107, 107, 0.1)' : 'transparent',
                  borderLeft: log.level === 'error' ? '3px solid #ff6b6b' : 'none',
                  '&:hover': {
                    backgroundColor: '#2d2d2d'
                  }
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: '#888',
                    minWidth: '60px',
                    flexShrink: 0,
                    fontSize: '0.75rem'
                  }}
                >
                  {new Date(log.timestamp).toLocaleTimeString()}
                </Typography>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: '0.875rem',
                      lineHeight: 1.4,
                      color: getLogColor(log.level)
                    }}
                  >
                    {getLogIcon(log.level)} {log.message}
                  </Typography>
                  {log.data && Object.keys(log.data).length > 0 && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#666',
                        display: 'block',
                        fontSize: '0.75rem',
                        fontStyle: 'italic',
                        mt: 0.5,
                        pl: 2
                      }}
                    >
                      {JSON.stringify(log.data, null, 2).substring(0, 200)}...
                    </Typography>
                  )}
                </Box>
              </Box>
            ))
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default EnhancedAnalysisLogPanel;
